import React, { useState, useEffect, useCallback } from 'react';
import './BusinessTracker.css';

const API = (type) => `/api/business?type=${type}`;

function BusinessTracker({ driverName, onBack }) {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [clients, setClients] = useState([]);
  const [orders, setOrders] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(null); // 'client', 'order', 'expense', or null
  const [editItem, setEditItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const authHeader = { 'Authorization': `Bearer ${driverName}`, 'Content-Type': 'application/json' };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [cRes, oRes, eRes] = await Promise.all([
        fetch(`${API('clients')}`, { headers: authHeader }),
        fetch(`${API('orders')}`, { headers: authHeader }),
        fetch(`${API('expenses')}`, { headers: authHeader })
      ]);
      const [cData, oData, eData] = await Promise.all([cRes.json(), oRes.json(), eRes.json()]);
      setClients(cData.clients || []);
      setOrders(oData.orders || []);
      setExpenses(eData.expenses || []);
    } catch (err) {
      console.error('Failed to load business data:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverName]);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── DASHBOARD TAB ───
  const renderDashboard = () => {
    const totalRevenue = orders.reduce((s, o) => s + (o.total_revenue || 0), 0);
    const totalCost = orders.reduce((s, o) => s + (o.total_cost || 0), 0);
    const totalExpenses = expenses.reduce((s, e) => s + (e.amount || 0), 0);
    const netProfit = totalRevenue - totalCost;
    const netAfterExpenses = netProfit - totalExpenses;
    const avgMargin = totalRevenue > 0 ? ((netProfit / totalRevenue) * 100).toFixed(1) : '0.0';

    // Per-technician breakdown
    const techStats = {};
    orders.forEach(o => {
      const tech = o.technician || 'Unknown';
      if (!techStats[tech]) techStats[tech] = { jobs: 0, revenue: 0, cost: 0 };
      techStats[tech].jobs++;
      techStats[tech].revenue += o.total_revenue || 0;
      techStats[tech].cost += o.total_cost || 0;
    });

    // Monthly breakdown
    const monthlyStats = {};
    orders.forEach(o => {
      const month = (o.date || '').substring(0, 7); // YYYY-MM
      if (!month) return;
      if (!monthlyStats[month]) monthlyStats[month] = { jobs: 0, revenue: 0, cost: 0 };
      monthlyStats[month].jobs++;
      monthlyStats[month].revenue += o.total_revenue || 0;
      monthlyStats[month].cost += o.total_cost || 0;
    });

    return (
      <div className="bt-dashboard">
        <div className="bt-stats-grid">
          <div className="bt-stat-card">
            <span className="bt-stat-label">Total Jobs</span>
            <span className="bt-stat-value">{orders.length}</span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Total Revenue</span>
            <span className="bt-stat-value bt-green">${totalRevenue.toFixed(0)}</span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Total Cost</span>
            <span className="bt-stat-value bt-red">${totalCost.toFixed(0)}</span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Net Profit</span>
            <span className={`bt-stat-value ${netProfit >= 0 ? 'bt-green' : 'bt-red'}`}>
              ${netProfit.toFixed(0)}
            </span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Expenses</span>
            <span className="bt-stat-value bt-orange">${totalExpenses.toFixed(0)}</span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">After Expenses</span>
            <span className={`bt-stat-value ${netAfterExpenses >= 0 ? 'bt-green' : 'bt-red'}`}>
              ${netAfterExpenses.toFixed(0)}
            </span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Avg Margin</span>
            <span className="bt-stat-value">{avgMargin}%</span>
          </div>
          <div className="bt-stat-card">
            <span className="bt-stat-label">Clients</span>
            <span className="bt-stat-value">{clients.length}</span>
          </div>
        </div>

        {/* Per-Technician */}
        {Object.keys(techStats).length > 0 && (
          <div className="bt-section">
            <h3 className="bt-section-title">By Technician</h3>
            <div className="bt-table-wrap">
              <table className="bt-table">
                <thead>
                  <tr><th>Tech</th><th>Jobs</th><th>Revenue</th><th>Cost</th><th>Profit</th></tr>
                </thead>
                <tbody>
                  {Object.entries(techStats).map(([tech, s]) => (
                    <tr key={tech}>
                      <td>{tech}</td>
                      <td>{s.jobs}</td>
                      <td className="bt-green">${s.revenue.toFixed(0)}</td>
                      <td className="bt-red">${s.cost.toFixed(0)}</td>
                      <td className={s.revenue - s.cost >= 0 ? 'bt-green' : 'bt-red'}>
                        ${(s.revenue - s.cost).toFixed(0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Monthly Breakdown */}
        {Object.keys(monthlyStats).length > 0 && (
          <div className="bt-section">
            <h3 className="bt-section-title">Monthly</h3>
            <div className="bt-table-wrap">
              <table className="bt-table">
                <thead>
                  <tr><th>Month</th><th>Jobs</th><th>Revenue</th><th>Cost</th><th>Profit</th><th>Margin</th></tr>
                </thead>
                <tbody>
                  {Object.entries(monthlyStats).sort().reverse().map(([month, s]) => (
                    <tr key={month}>
                      <td>{month}</td>
                      <td>{s.jobs}</td>
                      <td className="bt-green">${s.revenue.toFixed(0)}</td>
                      <td className="bt-red">${s.cost.toFixed(0)}</td>
                      <td className={s.revenue - s.cost >= 0 ? 'bt-green' : 'bt-red'}>
                        ${(s.revenue - s.cost).toFixed(0)}
                      </td>
                      <td>{s.revenue > 0 ? (((s.revenue - s.cost) / s.revenue) * 100).toFixed(0) : 0}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Recent Orders */}
        <div className="bt-section">
          <h3 className="bt-section-title">Recent Jobs</h3>
          <div className="bt-table-wrap">
            <table className="bt-table">
              <thead>
                <tr><th>#</th><th>Date</th><th>Client</th><th>Vehicle</th><th>Revenue</th><th>Profit</th><th>Tech</th></tr>
              </thead>
              <tbody>
                {orders.slice().reverse().slice(0, 10).map(o => (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td>{o.date}</td>
                    <td>{o.client_name}</td>
                    <td className="bt-nowrap">{o.vehicle}</td>
                    <td className="bt-green">${(o.total_revenue || 0).toFixed(0)}</td>
                    <td className={(o.total_revenue || 0) - (o.total_cost || 0) >= 0 ? 'bt-green' : 'bt-red'}>
                      ${((o.total_revenue || 0) - (o.total_cost || 0)).toFixed(0)}
                    </td>
                    <td>{o.technician}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ─── CLIENTS TAB ───
  const renderClients = () => {
    const filtered = clients.filter(c =>
      !searchTerm ||
      (c.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (c.phone || '').includes(searchTerm) ||
      (c.vehicle || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Enrich clients with order stats
    const enriched = filtered.map(c => {
      const clientOrders = orders.filter(o => o.client_id === c.id);
      return {
        ...c,
        job_count: clientOrders.length,
        total_revenue: clientOrders.reduce((s, o) => s + (o.total_revenue || 0), 0),
        total_cost: clientOrders.reduce((s, o) => s + (o.total_cost || 0), 0),
        last_service: clientOrders.length > 0 ?
          clientOrders.sort((a, b) => (b.date || '').localeCompare(a.date || ''))[0].date : '—'
      };
    });

    return (
      <div className="bt-clients">
        <div className="bt-toolbar">
          <input
            type="text"
            className="bt-search"
            placeholder="Search clients..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button className="bt-add-btn" onClick={() => { setEditItem(null); setShowForm('client'); }}>
            + Add Client
          </button>
        </div>
        <div className="bt-table-wrap">
          <table className="bt-table">
            <thead>
              <tr>
                <th>ID</th><th>Name</th><th>Phone</th><th>Vehicle</th>
                <th>Jobs</th><th>Revenue</th><th>Profit</th><th>Last Service</th><th>Tech</th><th></th>
              </tr>
            </thead>
            <tbody>
              {enriched.map(c => (
                <tr key={c.id}>
                  <td>{c.id}</td>
                  <td className="bt-bold">{c.name}</td>
                  <td className="bt-nowrap">{c.phone || '—'}</td>
                  <td className="bt-nowrap">{c.vehicle || '—'}</td>
                  <td>{c.job_count}</td>
                  <td className="bt-green">${c.total_revenue.toFixed(0)}</td>
                  <td className={(c.total_revenue - c.total_cost) >= 0 ? 'bt-green' : 'bt-red'}>
                    ${(c.total_revenue - c.total_cost).toFixed(0)}
                  </td>
                  <td>{c.last_service}</td>
                  <td>{c.technician || '—'}</td>
                  <td>
                    <button className="bt-edit-btn" onClick={() => { setEditItem(c); setShowForm('client'); }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {enriched.length === 0 && <p className="bt-empty">No clients yet</p>}
      </div>
    );
  };

  // ─── ORDERS TAB ───
  const renderOrders = () => {
    const filtered = orders.filter(o =>
      !searchTerm ||
      (o.client_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (o.vehicle || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
      <div className="bt-orders">
        <div className="bt-toolbar">
          <input
            type="text"
            className="bt-search"
            placeholder="Search orders..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
          <button className="bt-add-btn" onClick={() => { setEditItem(null); setShowForm('order'); }}>
            + Add Order
          </button>
        </div>
        <div className="bt-table-wrap">
          <table className="bt-table">
            <thead>
              <tr>
                <th>#</th><th>Date</th><th>Client</th><th>Vehicle</th>
                <th>Oil Change</th><th>Extras</th><th>Revenue</th>
                <th>Oil Cost</th><th>Filter</th><th>Gas</th><th>Driver</th><th>Total Cost</th>
                <th>Profit</th><th>Margin</th><th>Tech</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice().reverse().map(o => {
                const profit = (o.total_revenue || 0) - (o.total_cost || 0);
                const margin = o.total_revenue > 0 ? ((profit / o.total_revenue) * 100).toFixed(0) : 0;
                return (
                  <tr key={o.id}>
                    <td>{o.id}</td>
                    <td className="bt-nowrap">{o.date}</td>
                    <td className="bt-bold">{o.client_name}</td>
                    <td className="bt-nowrap">{o.vehicle}</td>
                    <td>${o.oil_change_price || 0}</td>
                    <td className="bt-nowrap">{o.extra_services || '—'}</td>
                    <td className="bt-green bt-bold">${(o.total_revenue || 0).toFixed(0)}</td>
                    <td>${o.oil_cost || 0}</td>
                    <td>${o.filter_cost || 0}</td>
                    <td>${o.gas_cost || 0}</td>
                    <td>${o.driver_income || 0}</td>
                    <td className="bt-red">${(o.total_cost || 0).toFixed(0)}</td>
                    <td className={profit >= 0 ? 'bt-green bt-bold' : 'bt-red bt-bold'}>
                      ${profit.toFixed(0)}
                    </td>
                    <td>{margin}%</td>
                    <td>{o.technician}</td>
                    <td>
                      <button className="bt-edit-btn" onClick={() => { setEditItem(o); setShowForm('order'); }}>
                        Edit
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Totals row */}
        {filtered.length > 0 && (
          <div className="bt-totals">
            <span>Totals: </span>
            <span className="bt-green bt-bold">
              Revenue: ${filtered.reduce((s, o) => s + (o.total_revenue || 0), 0).toFixed(0)}
            </span>
            <span className="bt-red">
              Cost: ${filtered.reduce((s, o) => s + (o.total_cost || 0), 0).toFixed(0)}
            </span>
            <span className="bt-green bt-bold">
              Profit: ${(filtered.reduce((s, o) => s + (o.total_revenue || 0), 0) - filtered.reduce((s, o) => s + (o.total_cost || 0), 0)).toFixed(0)}
            </span>
          </div>
        )}
        {filtered.length === 0 && <p className="bt-empty">No orders yet</p>}
      </div>
    );
  };

  // ─── EXPENSES TAB ───
  const renderExpenses = () => {
    const categories = ['Parts & Supplies', 'Gas', 'Car Repairs', 'Platform / Invoices', 'Business Loss', 'Other'];

    // Category totals
    const catTotals = {};
    categories.forEach(cat => {
      catTotals[cat] = expenses
        .filter(e => e.category === cat)
        .reduce((s, e) => s + (e.amount || 0), 0);
    });
    const totalExp = expenses.reduce((s, e) => s + (e.amount || 0), 0);

    return (
      <div className="bt-expenses">
        <div className="bt-toolbar">
          <div className="bt-expense-summary">
            {categories.filter(c => catTotals[c] !== 0).map(cat => (
              <span key={cat} className="bt-expense-tag">
                {cat}: <strong>${catTotals[cat].toFixed(0)}</strong>
              </span>
            ))}
            <span className="bt-expense-tag bt-expense-total">
              Total: <strong>${totalExp.toFixed(0)}</strong>
            </span>
          </div>
          <button className="bt-add-btn" onClick={() => { setEditItem(null); setShowForm('expense'); }}>
            + Add Expense
          </button>
        </div>
        <div className="bt-table-wrap">
          <table className="bt-table">
            <thead>
              <tr>
                <th>#</th><th>Date</th><th>Vendor</th><th>Description</th>
                <th>Category</th><th>Person</th><th>Amount</th><th></th>
              </tr>
            </thead>
            <tbody>
              {expenses.slice().reverse().map(e => (
                <tr key={e.id} className={e.amount < 0 ? 'bt-row-green' : e.category === 'Business Loss' ? 'bt-row-orange' : ''}>
                  <td>{e.id}</td>
                  <td className="bt-nowrap">{e.date}</td>
                  <td>{e.vendor}</td>
                  <td>{e.description}</td>
                  <td className="bt-nowrap">{e.category}</td>
                  <td>{e.person}</td>
                  <td className={e.amount < 0 ? 'bt-green' : 'bt-red'}>
                    {e.amount < 0 ? '-' : ''}${Math.abs(e.amount).toFixed(2)}
                  </td>
                  <td>
                    <button className="bt-edit-btn" onClick={() => { setEditItem(e); setShowForm('expense'); }}>
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {expenses.length === 0 && <p className="bt-empty">No expenses yet</p>}
      </div>
    );
  };

  // ─── FORMS ───
  const ClientForm = () => {
    const [form, setForm] = useState(editItem || {
      name: '', phone: '', email: '', address: '', vehicle: '', color: '',
      preferred_oil: '0W-20 Full Synthetic', technician: driverName
    });

    const handleSave = async () => {
      const endpoint = `${API('clients')}`;
      const method = editItem ? 'PUT' : 'POST';
      await fetch(endpoint, { method, headers: authHeader, body: JSON.stringify(form) });
      setShowForm(null);
      loadData();
    };

    const handleDelete = async () => {
      if (!window.confirm('Delete this client?')) return;
      await fetch(`${API('clients')}`, {
        method: 'DELETE', headers: authHeader, body: JSON.stringify({ id: editItem.id })
      });
      setShowForm(null);
      loadData();
    };

    return (
      <div className="bt-form-overlay">
        <div className="bt-form-card">
          <h3>{editItem ? 'Edit Client' : 'Add Client'}</h3>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Name *</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Phone</label>
              <input value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Email</label>
              <input value={form.email} onChange={e => setForm({...form, email: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Address</label>
              <input value={form.address} onChange={e => setForm({...form, address: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Vehicle</label>
              <input value={form.vehicle} onChange={e => setForm({...form, vehicle: e.target.value})} placeholder="2025 Toyota 4Runner" />
            </div>
            <div className="bt-form-group">
              <label>Color</label>
              <input value={form.color} onChange={e => setForm({...form, color: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Preferred Oil</label>
              <select value={form.preferred_oil} onChange={e => setForm({...form, preferred_oil: e.target.value})}>
                <option>0W-20 Full Synthetic</option>
                <option>5W-20 Full Synthetic</option>
                <option>5W-30 Full Synthetic</option>
                <option>0W-40 Full Synthetic</option>
                <option>5W-40 Full Synthetic</option>
                <option>10W-30 Full Synthetic</option>
                <option>15W-40 Diesel</option>
              </select>
            </div>
            <div className="bt-form-group">
              <label>Technician</label>
              <select value={form.technician} onChange={e => setForm({...form, technician: e.target.value})}>
                <option>Denis</option>
                <option>Iurii</option>
              </select>
            </div>
          </div>
          <div className="bt-form-actions">
            <button className="bt-cancel-btn" onClick={() => setShowForm(null)}>Cancel</button>
            {editItem && <button className="bt-delete-btn" onClick={handleDelete}>Delete</button>}
            <button className="bt-save-btn" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  const OrderForm = () => {
    const OIL_PRICE_PER_QT = 5;
    const [form, setForm] = useState(editItem || {
      date: new Date().toISOString().split('T')[0],
      client_id: '', client_name: '', vehicle: '',
      oil_change_price: 99, oil_filter_price: 0, engine_filter_price: 0, cabin_filter_price: 0,
      extra_services: '', total_revenue: 99,
      oil_capacity_qt: 5, oil_cost: 25, filter_cost: 8,
      engine_filter_cost: 0, cabin_filter_cost: 0,
      gas_cost: 6, driver_income: 50, total_cost: 89,
      technician: driverName, payment_method: 'Zelle'
    });

    const recalc = (f) => {
      const rev = (parseFloat(f.oil_change_price) || 0) + (parseFloat(f.oil_filter_price) || 0) +
                  (parseFloat(f.engine_filter_price) || 0) + (parseFloat(f.cabin_filter_price) || 0);
      const oilCost = (parseFloat(f.oil_capacity_qt) || 0) * OIL_PRICE_PER_QT;
      const cost = oilCost + (parseFloat(f.filter_cost) || 0) + (parseFloat(f.engine_filter_cost) || 0) +
                   (parseFloat(f.cabin_filter_cost) || 0) + (parseFloat(f.gas_cost) || 0) + (parseFloat(f.driver_income) || 0);
      return { ...f, total_revenue: rev, oil_cost: oilCost, total_cost: cost };
    };

    const updateField = (field, value) => {
      const updated = { ...form, [field]: value };
      setForm(recalc(updated));
    };

    // Auto-fill client info when client_id changes
    const selectClient = (clientId) => {
      const c = clients.find(cl => cl.id === parseInt(clientId));
      if (c) {
        setForm(recalc({
          ...form,
          client_id: c.id,
          client_name: c.name,
          vehicle: c.vehicle || ''
        }));
      } else {
        setForm({ ...form, client_id: clientId });
      }
    };

    const handleSave = async () => {
      const endpoint = `${API('orders')}`;
      const method = editItem ? 'PUT' : 'POST';
      const finalForm = recalc(form);
      await fetch(endpoint, { method, headers: authHeader, body: JSON.stringify(finalForm) });
      setShowForm(null);
      loadData();
    };

    const handleDelete = async () => {
      if (!window.confirm('Delete this order?')) return;
      await fetch(`${API('orders')}`, {
        method: 'DELETE', headers: authHeader, body: JSON.stringify({ id: editItem.id })
      });
      setShowForm(null);
      loadData();
    };

    const profit = (form.total_revenue || 0) - (form.total_cost || 0);

    return (
      <div className="bt-form-overlay">
        <div className="bt-form-card bt-form-wide">
          <h3>{editItem ? 'Edit Order' : 'Add Order'}</h3>

          <div className="bt-form-section-title">Order Info</div>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Date *</label>
              <input type="date" value={form.date} onChange={e => updateField('date', e.target.value)} />
            </div>
            <div className="bt-form-group">
              <label>Client</label>
              <select value={form.client_id} onChange={e => selectClient(e.target.value)}>
                <option value="">— Select Client —</option>
                {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="bt-form-group">
              <label>Client Name</label>
              <input value={form.client_name} onChange={e => updateField('client_name', e.target.value)} />
            </div>
            <div className="bt-form-group">
              <label>Vehicle</label>
              <input value={form.vehicle} onChange={e => updateField('vehicle', e.target.value)} />
            </div>
          </div>

          <div className="bt-form-section-title">Revenue</div>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Oil Change ($)</label>
              <input type="number" value={form.oil_change_price} onChange={e => updateField('oil_change_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Oil Filter ($)</label>
              <input type="number" value={form.oil_filter_price} onChange={e => updateField('oil_filter_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Engine Air Filter ($)</label>
              <input type="number" value={form.engine_filter_price} onChange={e => updateField('engine_filter_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Cabin Air Filter ($)</label>
              <input type="number" value={form.cabin_filter_price} onChange={e => updateField('cabin_filter_price', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Extra Services</label>
              <input value={form.extra_services} onChange={e => updateField('extra_services', e.target.value)} placeholder="e.g. Cabin Air Filter" />
            </div>
            <div className="bt-form-group">
              <label>Total Revenue</label>
              <input type="number" value={form.total_revenue} readOnly className="bt-readonly" />
            </div>
          </div>

          <div className="bt-form-section-title">Costs</div>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Oil Capacity (qt)</label>
              <input type="number" step="0.1" value={form.oil_capacity_qt} onChange={e => updateField('oil_capacity_qt', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Oil Cost (auto: qt x $5)</label>
              <input type="number" value={form.oil_cost} readOnly className="bt-readonly" />
            </div>
            <div className="bt-form-group">
              <label>Oil Filter Cost ($)</label>
              <input type="number" value={form.filter_cost} onChange={e => updateField('filter_cost', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Engine Filter Cost ($)</label>
              <input type="number" value={form.engine_filter_cost} onChange={e => updateField('engine_filter_cost', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Cabin Filter Cost ($)</label>
              <input type="number" value={form.cabin_filter_cost} onChange={e => updateField('cabin_filter_cost', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Gas / Trip ($)</label>
              <input type="number" value={form.gas_cost} onChange={e => updateField('gas_cost', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Driver Income ($)</label>
              <input type="number" value={form.driver_income} onChange={e => updateField('driver_income', parseFloat(e.target.value) || 0)} />
            </div>
            <div className="bt-form-group">
              <label>Total Cost</label>
              <input type="number" value={form.total_cost} readOnly className="bt-readonly" />
            </div>
          </div>

          <div className="bt-form-section-title">Details</div>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Technician</label>
              <select value={form.technician} onChange={e => updateField('technician', e.target.value)}>
                <option>Denis</option>
                <option>Iurii</option>
              </select>
            </div>
            <div className="bt-form-group">
              <label>Payment Method</label>
              <select value={form.payment_method} onChange={e => updateField('payment_method', e.target.value)}>
                <option>Zelle</option>
                <option>Cash</option>
                <option>Card</option>
                <option>Venmo</option>
              </select>
            </div>
          </div>

          <div className="bt-order-summary">
            <span>Revenue: <strong className="bt-green">${(form.total_revenue || 0).toFixed(0)}</strong></span>
            <span>Cost: <strong className="bt-red">${(form.total_cost || 0).toFixed(0)}</strong></span>
            <span>Profit: <strong className={profit >= 0 ? 'bt-green' : 'bt-red'}>${profit.toFixed(0)}</strong></span>
            <span>Margin: <strong>{form.total_revenue > 0 ? ((profit / form.total_revenue) * 100).toFixed(0) : 0}%</strong></span>
          </div>

          <div className="bt-form-actions">
            <button className="bt-cancel-btn" onClick={() => setShowForm(null)}>Cancel</button>
            {editItem && <button className="bt-delete-btn" onClick={handleDelete}>Delete</button>}
            <button className="bt-save-btn" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  const ExpenseForm = () => {
    const [form, setForm] = useState(editItem || {
      date: new Date().toISOString().split('T')[0],
      vendor: '', description: '',
      category: 'Parts & Supplies', person: driverName,
      amount: 0
    });

    const handleSave = async () => {
      const endpoint = `${API('expenses')}`;
      const method = editItem ? 'PUT' : 'POST';
      await fetch(endpoint, { method, headers: authHeader, body: JSON.stringify(form) });
      setShowForm(null);
      loadData();
    };

    const handleDelete = async () => {
      if (!window.confirm('Delete this expense?')) return;
      await fetch(`${API('expenses')}`, {
        method: 'DELETE', headers: authHeader, body: JSON.stringify({ id: editItem.id })
      });
      setShowForm(null);
      loadData();
    };

    return (
      <div className="bt-form-overlay">
        <div className="bt-form-card">
          <h3>{editItem ? 'Edit Expense' : 'Add Expense'}</h3>
          <div className="bt-form-grid">
            <div className="bt-form-group">
              <label>Date *</label>
              <input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
            </div>
            <div className="bt-form-group">
              <label>Vendor / Store</label>
              <input value={form.vendor} onChange={e => setForm({...form, vendor: e.target.value})} placeholder="O'Reilly Auto Parts" />
            </div>
            <div className="bt-form-group bt-form-full">
              <label>Description</label>
              <input value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Oil Filter MSL10358" />
            </div>
            <div className="bt-form-group">
              <label>Category</label>
              <select value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                <option>Parts & Supplies</option>
                <option>Gas</option>
                <option>Car Repairs</option>
                <option>Platform / Invoices</option>
                <option>Business Loss</option>
                <option>Other</option>
              </select>
            </div>
            <div className="bt-form-group">
              <label>Person</label>
              <select value={form.person} onChange={e => setForm({...form, person: e.target.value})}>
                <option>Denis</option>
                <option>Iurii</option>
              </select>
            </div>
            <div className="bt-form-group">
              <label>Amount ($) <small>(negative = return/credit)</small></label>
              <input type="number" step="0.01" value={form.amount} onChange={e => setForm({...form, amount: parseFloat(e.target.value) || 0})} />
            </div>
          </div>
          <div className="bt-form-actions">
            <button className="bt-cancel-btn" onClick={() => setShowForm(null)}>Cancel</button>
            {editItem && <button className="bt-delete-btn" onClick={handleDelete}>Delete</button>}
            <button className="bt-save-btn" onClick={handleSave}>Save</button>
          </div>
        </div>
      </div>
    );
  };

  // ─── MAIN RENDER ───
  return (
    <div className="bt-container">
      <div className="bt-header">
        <button className="bt-back" onClick={onBack}>← Jobs</button>
        <h2>Business</h2>
        <span className="bt-driver">{driverName}</span>
      </div>

      <div className="bt-tabs">
        {['dashboard', 'clients', 'orders', 'expenses'].map(tab => (
          <button
            key={tab}
            className={`bt-tab ${activeTab === tab ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab); setSearchTerm(''); }}
          >
            {tab === 'dashboard' && 'Dashboard'}
            {tab === 'clients' && `Clients (${clients.length})`}
            {tab === 'orders' && `Orders (${orders.length})`}
            {tab === 'expenses' && `Expenses (${expenses.length})`}
          </button>
        ))}
      </div>

      <div className="bt-content">
        {loading ? (
          <div className="bt-loading">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'dashboard' && renderDashboard()}
            {activeTab === 'clients' && renderClients()}
            {activeTab === 'orders' && renderOrders()}
            {activeTab === 'expenses' && renderExpenses()}
          </>
        )}
      </div>

      {showForm === 'client' && <ClientForm />}
      {showForm === 'order' && <OrderForm />}
      {showForm === 'expense' && <ExpenseForm />}
    </div>
  );
}

export default BusinessTracker;
